import type { overview as en } from '@/i18n/locales/en/overview';
import type { Translation } from '@/i18n/types';

export const overview: Translation<typeof en> = {
  page: {
    title: 'Panoramica',
    lead: 'Come sta andando {{company}}?',
    noScope: "Seleziona un'azienda per vedere la panoramica.",
    noDepartments: 'Nessun reparto qui ha statistiche che puoi vedere.',
    disclaimer:
      'Tutti i valori sono indicazioni di benessere a livello di gruppo e non costituiscono una diagnosi medica.',
  },
  summary: {
    caption: 'Riepilogo IA basato su dati anonimizzati e aggregati.',
  },
  figures: {
    vitality: 'Vitalità',
    stress: 'Stress',
    sleep: 'Sonno',
    engagement: 'Coinvolgimento',
    activeParticipants: 'Partecipanti attivi',
  },
  figuresInfo: {
    vitality: 'Un punteggio composito basato su sonno, attività, HRV, stress e frequenza cardiaca.',
    stress: 'Livello medio di stress, misurato direttamente dal wearable.',
    sleep: 'Durata media del sonno per notte, misurata direttamente dal wearable.',
    engagement: "Percentuale di dipendenti che usano attivamente l'app.",
    activeParticipants: "Numero di dipendenti attivi nell'ultimo periodo.",
  },
  legend: {
    title: 'Cosa stai vedendo',
    measured:
      'Misurato direttamente — direttamente dal wearable NOLATR o da un sensore collegato. Massima affidabilità.',
    aiDerived:
      "Derivato dall'IA — non è una misurazione diretta. L'IA combina più segnali in un indicatore.",
    usage: "Uso e comportamento — interazioni reali nell'app, non una misurazione della salute.",
  },
  developments: {
    title: 'Sviluppi principali',
    empty: 'Nessuno sviluppo rilevante in questo periodo.',
    stress: {
      title: 'Opportunità di offrire un momento di pausa (stress elevato)',
      body: 'Un momento di relax amichevole o un consiglio sulla respirazione può aiutare il team a ricaricarsi.',
    },
    sleep: {
      title: "Opportunità: sonno al di sotto dell'obiettivo",
      body: 'Un consiglio di recupero mirato e positivo può sostenere bene il team.',
    },
    vitalityGood: {
      title: 'La vitalità sta evolvendo bene',
      body: 'Il team sta mantenendo bene il ritmo attuale — un buon momento per riconoscerlo.',
    },
    needsReactivation: {
      title: '{{count}} persone necessitano di riattivazione',
      body: 'Un messaggio di riattivazione breve e personale di solito funziona meglio.',
    },
    mostActiveWeekday: {
      title: 'Più energia: {{weekday}}',
      body: 'Pianifica sfide e comunicazioni intorno al giorno più attivo per la massima diffusione.',
    },
    challengeParticipation: {
      title: 'Partecipazione alla sfida {{pct}}%',
      body: 'Una nuova sfida di team a basso impegno potrebbe coinvolgere più persone.',
    },
  },
  actions: {
    title: 'Azioni per HR',
    empty: 'Nessuna azione consigliata al momento.',
    sendReminder: {
      title: '{{count}} dipendenti non ancora attivati',
      subtitle: 'Invia un promemoria',
    },
    reactivate: {
      title: '{{count}} dipendenti necessitano di riattivazione',
      subtitle: 'Contattali',
    },
    stressCheckIn: {
      title: 'Stress elevato nel team',
      subtitle: 'Valuta un breve confronto sul carico di lavoro',
    },
    sleepReminder: {
      title: "Sonno al di sotto dell'obiettivo nel team",
      subtitle: 'Valuta un promemoria sulle routine serali e sul tempo davanti allo schermo',
    },
    activeChallenge: {
      title: "'{{name}}' è in corso — pianifica la prossima",
      subtitle: 'Sfide',
    },
    pilotEvaluation: {
      interim: {
        title: "Il pilota '{{name}}' è pronto per la valutazione intermedia",
        subtitle: 'Visualizza pilota',
      },
      final: {
        title: "Il pilota '{{name}}' è pronto per la valutazione finale",
        subtitle: 'Visualizza pilota',
      },
    },
  },
  energy: {
    title: 'Energia e benessere lavorativo',
    subtitle:
      'Come il team vive la giornata lavorativa — energia, pause, ritmo di lavoro e coinvolgimento. Solo aggregato, mai individuale.',
    badge: 'Aggregato e anonimizzato',
    tbd: 'Da determinare',
    missingData: {
      noSource: 'Indice di energia ed eNPS: fonte dei dati non ancora determinata.',
      insufficientParticipants: 'Partecipanti insufficienti per {{metrics}}.',
    },
    privacyNote:
      'Informazioni di supporto, non monitoraggio. Benessere indicativo a livello di gruppo — nessuna interpretazione medica.',
    metrics: {
      energyIndex: 'Indice di energia',
      breakAdoption: 'Adozione delle pause',
      focusRhythm: 'Ritmo di concentrazione',
      coachAdoption: 'Adozione del coach',
      enps: 'eNPS',
    },
  },
  pilots: {
    title: 'Stato dei piloti',
    empty: 'Nessun pilota ancora.',
    participantsAndDay: '{{count}} partecipanti · giorno {{day}} di {{total}}',
    viewReport: 'Visualizza il report del pilota',
  },
  challenges: {
    title: 'Sfide attive',
    empty: 'Nessuna sfida ancora.',
    active: 'Attive',
    total: 'Totale',
    participants: 'Partecipanti',
    activeBadge: '{{count}} attive',
    manageLink: 'Gestisci le sfide',
    noneActive: 'Nessuna sfida attiva al momento.',
    participantsCount: '{{count}} partecipanti',
  },
  trends: {
    title: 'Tendenze',
    filters: {
      allDepartments: 'Tutti i reparti',
      participants: {
        label: 'Partecipanti',
        all: 'Tutti i partecipanti',
        manager: 'Responsabili',
        employee: 'Dipendenti',
      },
    },
    vitalityScore: 'Punteggio di vitalità nel tempo',
    activeUsersPerWeek: 'Utenti attivi a settimana',
    sleep: 'Tendenza del sonno',
    stress: 'Tendenza dello stress',
    hrv: 'Tendenza HRV',
    recovery: 'Tendenza di recupero',
    activity: "Tendenza dell'attività",
    notAvailable:
      'Nessuna fonte dati per questa tendenza ancora — non disponibile in questa versione.',
    vsPreviousPeriod: 'rispetto al periodo precedente',
    info: {
      vitalityScore:
        'Un punteggio di vitalità composito al giorno, basato su tutti i segnali di vitalità.',
      activeUsersPerWeek:
        "Numero di dipendenti unici che hanno usato attivamente l'app ogni settimana.",
      sleep: 'Durata media del sonno al giorno, misurata direttamente dal wearable.',
      stress: 'Livello medio di stress al giorno, misurato direttamente dal wearable.',
      hrv: 'Variabilità media della frequenza cardiaca (HRV) al giorno, un indicatore di recupero.',
      recovery: 'Un punteggio di recupero composito al giorno, basato su sonno, HRV e attività.',
      activity: 'Numero medio di passi al giorno, misurato direttamente dal wearable.',
    },
  },
};
